import React, { useEffect, useState, useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import ServerPagination from '../components/ServerPagination';
import Filters from '../components/Filters';
import AccordionList from '../components/AccordionList';
import ExportButtons from '../components/ExportButtons';
import PageSizeSelector from '../components/PageSizeSelector';
import { PaymentStatusBadge, GatewayBadge, CurrencyBadge, AmountDisplay, ProductBadge } from '../components/Badges';
import { getPaymentsWithFilters, setFilters, resetFilters } from '../redux/paymentSlice';
import { fetchPaymentsWithFilters } from '../services/api';
import { handleExportResponse } from '../utils/exportUtils';
import axiosInstance from '../services/axiosInstance';
import RecordsLoader from '../components/RecordsLoader';

const PaymentsList = () => {
    const dispatch = useDispatch();
    const { payments, status, pagination, error } = useSelector((state) => state.payments);

    const [currentPage, setCurrentPage] = useState(1);
    const [pageSize, setPageSize] = useState(20);
    const [showFilters, setShowFilters] = useState(false);
    const [localFilters, setLocalFilters] = useState({
        organization_email: '',
        organization_title: '',
        first_name: '',
        last_name: '',
        payment_status: '',
        payment_date_from: '',
        payment_date_to: '',
        amount_min: '',
        amount_max: '',
        currency: '',
        plan_id: '',
        product: '',
    });

    // Filter configuration
    const filterConfig = [
        {
            key: 'organization_email',
            label: 'Organization Email',
            type: 'text',
            placeholder: 'Enter organization email',
        },
        {
            key: 'organization_title',
            label: 'Organization Title',
            type: 'text',
            placeholder: 'Enter organization title',
        },
        {
            key: 'first_name',
            label: 'First Name',
            type: 'text',
            placeholder: 'Enter first name',
        },
        {
            key: 'last_name',
            label: 'Last Name',
            type: 'text',
            placeholder: 'Enter last name',
        },
        {
            key: 'payment_status',
            label: 'Payment Status',
            type: 'select',
            options: [
                { value: 'paid', label: 'Paid' },
                { value: 'pending', label: 'Pending' },
                { value: 'failed', label: 'Failed' },
            ],
        },
        {
            key: 'payment_date_from',
            label: 'Payment Date From',
            type: 'date',
        },
        {
            key: 'payment_date_to',
            label: 'Payment Date To',
            type: 'date',
        },
        {
            key: 'amount_min',
            label: 'Min Amount',
            type: 'number',
            placeholder: 'Enter minimum amount',
            step: '0.01',
            min: 0,
        },
        {
            key: 'amount_max',
            label: 'Max Amount',
            type: 'number',
            placeholder: 'Enter maximum amount',
            step: '0.01',
            min: 0,
        },
        {
            key: 'currency',
            label: 'Currency',
            type: 'text',
            placeholder: 'Enter currency (e.g., USD)',
        },
        {
            key: 'plan_id',
            label: 'Plan ID',
            type: 'number',
            placeholder: 'Enter plan ID',
            min: 0,
        },
        {
            key: 'product',
            label: 'Product',
            type: 'text',
            placeholder: 'Enter product name (e.g., Listing)',
        },
    ];

    // Fetch payments with current filters and pagination
    const fetchPayments = useCallback(() => {
        const params = {
            page: currentPage,
            page_size: pageSize,
            ...localFilters,
        };
        
        // Remove empty filter values
        Object.keys(params).forEach(key => {
            if (params[key] === '' || params[key] === null || params[key] === undefined) {
                delete params[key];
            }
        });

        dispatch(getPaymentsWithFilters(params));
    }, [dispatch, currentPage, pageSize, localFilters]);

    useEffect(() => {
        fetchPayments();
    }, [fetchPayments]);

    const handleFilterChange = (filters) => {
        setLocalFilters(filters);
        dispatch(setFilters(filters));
        setCurrentPage(1); // Reset to first page when filters change
    };

    const handleFilterReset = (filters) => {
        setLocalFilters(filters);
        dispatch(resetFilters());
        setCurrentPage(1);
    };

    const handlePageChange = (page) => {
        setCurrentPage(page);
    };

    const handlePageSizeChange = (e) => {
        setPageSize(Number(e.target.value));
        setCurrentPage(1);
    };

    const formatDate = (dateString) => {
        if (!dateString) return '—';
        const date = new Date(dateString);
        return date.toLocaleDateString('en-US', {
            year: 'numeric',
            month: 'short',
            day: 'numeric',
        });
    };

    const handleExport = async (format) => {
        try {
            // Get filters without empty values
            const exportParams = { ...localFilters, export: format };
            Object.keys(exportParams).forEach(key => {
                if (exportParams[key] === '' || exportParams[key] === null || exportParams[key] === undefined) {
                    delete exportParams[key];
                }
            });

            const response = await fetchPaymentsWithFilters(exportParams);
            handleExportResponse(response, format, `payments.${format}`);
        } catch (error) {
            console.error('Export error:', error);
            alert(error.message || 'Failed to export payments. Please try again.');
        }
    };

    const handleInvoiceView = (invoicePath) => {
        if (!invoicePath) return;
        
        // Get base URL and construct full path
        const baseUrl = axiosInstance.defaults.baseURL || '';
        const fullUrl = invoicePath.startsWith('http') 
            ? invoicePath 
            : `${baseUrl}${invoicePath.startsWith('/') ? invoicePath.slice(1) : invoicePath}`;
        
        // Open in new tab
        window.open(fullUrl, '_blank');
    };

    return (
        <div className="container mx-auto p-6">
            {status === 'loading' && <RecordsLoader text="Loading Payments, please hold on..." />}

            <div className="flex items-center justify-between mb-6">
                <h1 className="text-2xl text-black font-bold">Payments List</h1>
                <button
                    onClick={() => setShowFilters(!showFilters)}
                    className="px-4 py-2 bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-lg transition-colors font-medium flex items-center gap-2"
                >
                    <svg
                        className={`w-5 h-5 transition-transform duration-200 ${showFilters ? 'transform rotate-180' : ''}`}
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                    >
                        <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M19 9l-7 7-7-7"
                        />
                    </svg>
                    {showFilters ? 'Hide Filters' : 'Show Filters'}
                </button>
            </div>

            {/* Filters */}
            {showFilters && (
                <div className="mb-4">
                    <Filters
                        filters={filterConfig}
                        onFilterChange={handleFilterChange}
                        onReset={handleFilterReset}
                    />
                </div>
            )}

            {/* Export Buttons */}
            <div className="mb-4">
                <ExportButtons
                    onExport={handleExport}
                    isLoading={status === 'loading'}
                />
            </div>

            {/* Page Size Selector */}
            <div className="mb-4">
                <PageSizeSelector
                    pageSize={pageSize}
                    onPageSizeChange={handlePageSizeChange}
                />
            </div>

            {status === 'failed' && (
                <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded mb-4">
                    Error loading Payments: {error || 'Unknown error'}
                </div>
            )}

            {/* Payments Accordion List */}
            {status === 'succeeded' && (
                <>
                    <AccordionList
                        data={payments || []}
                        getRowKey={(payment) => payment.payment_transaction_id}
                        primaryFields={[
                            {
                                key: 'payment_transaction_id',
                                label: 'Payment ID',
                            },
                            {
                                key: 'order_id',
                                label: 'Order ID',
                                render: (value, item) => {
                                    const gateway = item.payment_gateway?.toLowerCase();
                                    const orderId = gateway === 'paypal' 
                                        ? item.paypal_order_id 
                                        : item.razorpay_order_id;
                                    return (
                                        <span className="font-mono break-all">
                                            {orderId || '—'}
                                        </span>
                                    );
                                },
                                mono: true,
                            },
                            {
                                key: 'organization_title',
                                label: 'Organization',
                                render: (value, item) => (
                                    <div>
                                        <div className="font-medium text-gray-900">
                                            {value || '—'}
                                        </div>
                                        {item.organization_email && (
                                            <div className="text-xs text-gray-500 mt-0.5">
                                                {item.organization_email}
                                            </div>
                                        )}
                                    </div>
                                ),
                                flex: true,
                            },
                            {
                                key: 'amount',
                                label: 'Amount',
                                render: (value, item) => (
                                    <AmountDisplay amount={value} currency={item.currency} />
                                ),
                                highlight: true,
                            },
                            {
                                key: 'payment_gateway',
                                label: 'Gateway',
                                render: (value) => <GatewayBadge gateway={value} />,
                            },
                            {
                                key: 'payment_status',
                                label: 'Status',
                                render: (value) => <PaymentStatusBadge status={value} />,
                            },
                            {
                                key: 'invoice_path',
                                label: '',
                                render: (value, item) => {
                                    if (!value) return null;
                                    
                                    return (
                                        <button
                                            onClick={(e) => {
                                                e.stopPropagation();
                                                handleInvoiceView(value);
                                            }}
                                            className="inline-flex items-center justify-center w-8 h-8 rounded-full bg-blue-50 hover:bg-blue-100 text-blue-700 transition-all duration-200 hover:scale-110 border border-blue-200"
                                            title="View Invoice"
                                        >
                                            <svg
                                                className="w-4 h-4"
                                                fill="none"
                                                stroke="currentColor"
                                                viewBox="0 0 24 24"
                                            >
                                                <path
                                                    strokeLinecap="round"
                                                    strokeLinejoin="round"
                                                    strokeWidth={2}
                                                    d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
                                                />
                                            </svg>
                                        </button>
                                    );
                                },
                            },
                        ]}
                        expandedFields={[
                            {
                                key: 'first_name',
                                label: 'Name',
                                render: (value, item) =>
                                    item.first_name || item.last_name
                                        ? `${item.first_name || ''} ${item.last_name || ''}`.trim()
                                        : '—',
                            },
                            {
                                key: 'currency',
                                label: 'Currency',
                                render: (value) => <CurrencyBadge currency={value} />,
                            },
                            {
                                key: 'receipt',
                                label: 'Receipt',
                                mono: true,
                            },
                            {
                                key: 'payment_created_at',
                                label: 'Payment Date',
                                render: (value) => formatDate(value),
                            },
                            {
                                key: 'organization_type',
                                label: 'Organization Type',
                                render: (value) => value || '—',
                            },
                            {
                                key: 'location',
                                label: 'Location',
                                render: (value, item) =>
                                    [item.city, item.country].filter(Boolean).join(', ') || '—',
                            },
                            {
                                key: 'product',
                                label: 'Product',
                                render: (value, item) => <ProductBadge product={item.product} />,
                            },
                        ]}
                        emptyMessage="No payments found!"
                        allowMultipleExpand={true}
                    />

                    {/* Server-side Pagination */}
                    {pagination && pagination.total_pages > 0 && (
                        <ServerPagination
                            currentPage={pagination.page || currentPage}
                            totalPages={pagination.total_pages || 1}
                            onPageChange={handlePageChange}
                            totalCount={pagination.total_count || 0}
                            pageSize={pagination.page_size || pageSize}
                            showPageInfo={true}
                        />
                    )}
                </>
            )}
        </div>
    );
};

export default PaymentsList;
